import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { userModule } from "../../api/modules/user.module";
import { addressModule } from "../../api/modules/address.module";
import { showToast } from "../../utils/toast";
import { track } from "../../analytics/track";
import { EVENTS } from "../../analytics/events";
import SellerRegistrationView from "./SellerRegistration.view";

/**
 * /seller-registration, the container. The two fetches, the two submits, the
 * analytics event and the redirect.
 *
 * Everything visual lives in `SellerRegistration.view.jsx`, which is pure, so
 * the state explorer can mount every step and every validation state without
 * the API running.
 *
 * PRIVACY: the previous version console.logged the whole registration payload
 * (name, phone number, address id) and then logged the API response beside it.
 * Both are gone. Nothing in this file logs user data.
 */

export default function SellerRegistration() {
  const navigate = useNavigate();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [addressModalLoading, setAddressModalLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await userModule.getSellerProfile();
      if (response.success) setUserProfile(response.data);
    } catch (error) {
      showToast.error("Failed to load your profile");
    }
  }, []);

  const fetchUserAddresses = useCallback(async () => {
    setLoadingAddress(true);
    try {
      const response = await addressModule.getAddresses();
      if (response.success) {
        const defaultAddr = (response.data || []).find((addr) => addr.is_default);

        if (defaultAddr) {
          setDefaultAddress(defaultAddr);
        } else {
          // No default address found, show the dialog
          setShowAddressModal(true);
        }
      } else {
        showToast.error(response.message || "Failed to fetch addresses");
        setShowAddressModal(true);
      }
    } catch (error) {
      showToast.error("Failed to fetch addresses");
      setShowAddressModal(true);
    } finally {
      setLoadingAddress(false);
    }
  }, []);

  useEffect(() => {
    fetchUserAddresses();
    fetchUserProfile();
  }, [fetchUserAddresses, fetchUserProfile]);

  const handleAddressSubmit = async (values, { setSubmitting }) => {
    setAddressModalLoading(true);
    try {
      const response = await addressModule.addAddress(values);
      if (response.success) {
        showToast.success("Default address added successfully");
        setDefaultAddress(response.data);
        setShowAddressModal(false);
      } else {
        showToast.error(response.message || "Failed to add address");
      }
    } catch (error) {
      showToast.error("Failed to add address");
    } finally {
      setAddressModalLoading(false);
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!defaultAddress) {
      showToast.error("Please add a default address first");
      setShowAddressModal(true);
      setSubmitting(false);
      return;
    }

    const response = await userModule.sellerRegister({
      ...values,
      default_address_id: defaultAddress.id,
    });

    if (response.success) {
      track(EVENTS.SELLER_APPLICATION_SUBMITTED, {
        store_name: values.store_name,
      });
      showToast.success(response.message || "Registration successful");
      navigate("/registration-complete", { replace: true });
    } else {
      showToast.error(response.message || "Registration failed");
    }
    setSubmitting(false);
  };

  return (
    <SellerRegistrationView
      userProfile={userProfile}
      defaultAddress={defaultAddress}
      loadingAddress={loadingAddress}
      onSubmit={handleSubmit}
      addressModalOpen={showAddressModal}
      addressModalLoading={addressModalLoading}
      onOpenAddressModal={() => setShowAddressModal(true)}
      onCloseAddressModal={() => setShowAddressModal(false)}
      onSubmitAddress={handleAddressSubmit}
    />
  );
}
